// The roast curve.
//
// Two stacked charts sharing an x-axis (Recharts `syncId`):
//
//   1. Temperatures — BT and IBTS on the left axis, RoR on the right. Two
//      y-scales on one plot is the roasting convention (Artisan, RoasTime
//      and every roast log the operator has ever read put RoR on the right),
//      so it is kept here deliberately; RoR is drawn thin and dashed so it
//      never reads as a third temperature.
//   2. Controls — power / fan / drum as step lines on a 0–9 axis. A knob
//      keeps its setting until it moves, so `stepAfter` is the honest shape.
//
// Milestones are vertical reference lines on both charts. Each label carries
// type, provenance and confidence together: on this operator's history
// most first-crack marks are recipe auto-stamps, and a tick labelled
// "FC" alone would let a plan value read as an observation (issue #8).
//
// Colours are CSS tokens (index.css `--chart-*`) so dark mode is a
// selected palette, not an inverted one. A visually hidden text summary
// carries the same information for screen readers.

import { useId, useMemo } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { DetectedMilestone, Timeseries } from "../types";
import {
  buildChartRows,
  chartMilestones,
  chartTextSummary,
  controlAt,
  formatControls,
  type ChartMilestone,
  type ChartRow,
} from "../chart";
import { formatConfidence, formatTime } from "../format";

const SERIES = {
  bt: "var(--chart-bt)",
  ibts: "var(--chart-ibts)",
  ror: "var(--chart-ror)",
  power: "var(--chart-power)",
  fan: "var(--chart-fan)",
  drum: "var(--chart-drum)",
  grid: "var(--border)",
  text: "var(--fg-muted)",
  marker: "var(--fg-muted)",
} as const;

export interface RoastChartProps {
  ts: Timeseries;
  milestones: DetectedMilestone[];
  unit: "C" | "F";
}

export function RoastChart({ ts, milestones, unit }: RoastChartProps) {
  const rows = useMemo(() => buildChartRows(ts), [ts]);
  const marks = useMemo(() => chartMilestones(milestones), [milestones]);
  const summary = useMemo(
    () => chartTextSummary(ts, marks, unit),
    [ts, marks, unit],
  );
  const syncId = useId();
  const descId = useId();

  if (rows.length === 0) {
    return <p className="muted">No samples to draw.</p>;
  }

  const rorLabel = ts.ror_source
    ? `RoR ${ts.ror_source.toUpperCase()} (°${unit}/min)`
    : `RoR (°${unit}/min)`;

  return (
    <figure
      className="roast-chart"
      role="img"
      aria-label="Roast curve: bean temperature, IBTS, rate of rise, controls and milestones"
      aria-describedby={descId}
    >
      <p id={descId} className="sr-only">
        {summary}
      </p>

      <div className="roast-chart-temps" aria-hidden="true">
        <ResponsiveContainer>
          <LineChart
            data={rows}
            syncId={syncId}
            margin={{ top: 28, right: 12, bottom: 4, left: 0 }}
          >
            <CartesianGrid stroke={SERIES.grid} strokeOpacity={0.6} vertical={false} />
            <XAxis
              dataKey="t"
              type="number"
              domain={["dataMin", "dataMax"]}
              tickFormatter={(v: number) => formatTime(v)}
              tick={{ fill: SERIES.text, fontSize: 11 }}
              stroke={SERIES.grid}
              minTickGap={40}
            />
            <YAxis
              yAxisId="temp"
              domain={["auto", "auto"]}
              tick={{ fill: SERIES.text, fontSize: 11 }}
              stroke={SERIES.grid}
              width={44}
              label={{
                value: `°${unit}`,
                angle: -90,
                position: "insideLeft",
                fill: SERIES.text,
                fontSize: 11,
              }}
            />
            <YAxis
              yAxisId="ror"
              orientation="right"
              domain={["auto", "auto"]}
              tick={{ fill: SERIES.text, fontSize: 11 }}
              stroke={SERIES.grid}
              width={40}
              label={{
                value: `°${unit}/min`,
                angle: 90,
                position: "insideRight",
                fill: SERIES.text,
                fontSize: 11,
              }}
            />
            <Tooltip
              content={<ChartTooltip rows={rows} marks={marks} unit={unit} />}
              isAnimationActive={false}
              cursor={{ stroke: SERIES.marker, strokeDasharray: "2 2" }}
            />
            <Legend
              verticalAlign="top"
              align="left"
              iconType="plainline"
              wrapperStyle={{ fontSize: 12, color: "var(--fg)", top: 0 }}
            />
            {marks.map((m) => (
              <ReferenceLine
                key={m.type}
                yAxisId="temp"
                x={m.time_s}
                stroke={SERIES.marker}
                strokeDasharray="3 3"
                strokeOpacity={0.8}
                label={{
                  value: m.short,
                  position: "insideTopRight",
                  angle: -90,
                  fill: SERIES.text,
                  fontSize: 10,
                  offset: 8,
                }}
              />
            ))}
            <Line
              yAxisId="temp"
              dataKey="bt"
              name={`BT (°${unit})`}
              stroke={SERIES.bt}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2, stroke: "var(--bg)" }}
              isAnimationActive={false}
              strokeWidth={2}
              connectNulls
            />
            <Line
              yAxisId="temp"
              dataKey="ibts"
              name={`IBTS (°${unit})`}
              stroke={SERIES.ibts}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2, stroke: "var(--bg)" }}
              isAnimationActive={false}
              strokeWidth={2}
              connectNulls
            />
            <Line
              yAxisId="ror"
              dataKey="ror"
              name={rorLabel}
              stroke={SERIES.ror}
              strokeDasharray="5 3"
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2, stroke: "var(--bg)" }}
              isAnimationActive={false}
              strokeWidth={1.5}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="roast-chart-controls" aria-hidden="true">
        <ResponsiveContainer>
          <LineChart
            data={rows}
            syncId={syncId}
            margin={{ top: 4, right: 52, bottom: 4, left: 0 }}
          >
            <CartesianGrid stroke={SERIES.grid} strokeOpacity={0.6} vertical={false} />
            <XAxis
              dataKey="t"
              type="number"
              domain={["dataMin", "dataMax"]}
              tickFormatter={(v: number) => formatTime(v)}
              tick={{ fill: SERIES.text, fontSize: 11 }}
              stroke={SERIES.grid}
              minTickGap={40}
            />
            <YAxis
              domain={[0, 9]}
              ticks={[0, 3, 6, 9]}
              tick={{ fill: SERIES.text, fontSize: 11 }}
              stroke={SERIES.grid}
              width={44}
              label={{
                value: "setting",
                angle: -90,
                position: "insideLeft",
                fill: SERIES.text,
                fontSize: 11,
              }}
            />
            <Tooltip
              content={<ChartTooltip rows={rows} marks={marks} unit={unit} />}
              isAnimationActive={false}
              cursor={{ stroke: SERIES.marker, strokeDasharray: "2 2" }}
            />
            <Legend
              verticalAlign="bottom"
              align="left"
              iconType="plainline"
              wrapperStyle={{ fontSize: 12, color: "var(--fg)" }}
            />
            {marks.map((m) => (
              <ReferenceLine
                key={m.type}
                x={m.time_s}
                stroke={SERIES.marker}
                strokeDasharray="3 3"
                strokeOpacity={0.8}
              />
            ))}
            <Line
              dataKey="power"
              name="Power"
              type="stepAfter"
              stroke={SERIES.power}
              dot={false}
              isAnimationActive={false}
              strokeWidth={2}
              connectNulls
            />
            <Line
              dataKey="fan"
              name="Fan"
              type="stepAfter"
              stroke={SERIES.fan}
              strokeDasharray="6 3"
              dot={false}
              isAnimationActive={false}
              strokeWidth={2}
              connectNulls
            />
            <Line
              dataKey="drum"
              name="Drum"
              type="stepAfter"
              stroke={SERIES.drum}
              strokeDasharray="2 3"
              dot={false}
              isAnimationActive={false}
              strokeWidth={2}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <figcaption className="roast-chart-legend small muted">
        {marks.length === 0 ? (
          "No milestone has an effective time on this roast."
        ) : (
          <>
            Milestones:{" "}
            {marks.map((m, i) => (
              <span key={m.type}>
                {i > 0 && " · "}
                <span className="mono">{formatTime(m.time_s)}</span> {m.label}
              </span>
            ))}
          </>
        )}
      </figcaption>
    </figure>
  );
}

// Hover card: every series at this sample plus the control state in
// force, and any milestone sitting on this sample.
interface TooltipProps {
  active?: boolean;
  label?: number | string;
  payload?: Array<{ payload?: ChartRow }>;
  rows: ChartRow[];
  marks: ChartMilestone[];
  unit: "C" | "F";
}

function ChartTooltip({ active, label, payload, rows, marks, unit }: TooltipProps) {
  if (!active || label == null) return null;
  const t = Number(label);
  const row = payload?.[0]?.payload ?? rows.find((r) => r.t === t);
  if (!row) return null;
  const controls = controlAt(rows, t);
  // Sample spacing decides what "on this sample" means.
  const step = rows.length > 1 ? Math.max(1, rows[1].t - rows[0].t) : 1;
  const here = marks.filter((m) => Math.abs(m.time_s - t) <= step / 2);
  return (
    <div className="chart-tooltip">
      <div className="chart-tooltip-title mono">{formatTime(t)}</div>
      <table>
        <tbody>
          <TooltipRow swatch={SERIES.bt} name="BT" value={fmtTemp(row.bt, unit)} />
          <TooltipRow swatch={SERIES.ibts} name="IBTS" value={fmtTemp(row.ibts, unit)} />
          <TooltipRow
            swatch={SERIES.ror}
            name="RoR"
            value={row.ror != null ? `${row.ror.toFixed(1)} °${unit}/min` : "–"}
            dashed
          />
          <tr>
            <td colSpan={2} className="muted small" style={{ paddingTop: 6 }}>
              controls
            </td>
            <td className="mono" style={{ paddingTop: 6 }}>
              {formatControls(controls)}
            </td>
          </tr>
        </tbody>
      </table>
      {here.map((m) => (
        <div key={m.type} className="chart-tooltip-mark small">
          <strong>{m.label.split(" · ")[0]}</strong>
          <span className="muted"> · {m.label.split(" · ").slice(1).join(" · ")}</span>
          <span className={`badge ${formatConfidence(m.confidence).cls}`} style={{ marginLeft: 6 }}>
            {m.source.replace(/_/g, " ")}
          </span>
        </div>
      ))}
    </div>
  );
}

function TooltipRow({
  swatch,
  name,
  value,
  dashed,
}: {
  swatch: string;
  name: string;
  value: string;
  dashed?: boolean;
}) {
  return (
    <tr>
      <td style={{ width: 18 }}>
        <span
          className="chart-swatch"
          style={{
            background: dashed ? "transparent" : swatch,
            borderTop: dashed ? `2px dashed ${swatch}` : undefined,
          }}
        />
      </td>
      <td className="muted small">{name}</td>
      <td className="mono">{value}</td>
    </tr>
  );
}

function fmtTemp(v: number | null, unit: "C" | "F"): string {
  return v == null ? "–" : `${v.toFixed(1)} °${unit}`;
}
